import React from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { Lock, User, Phone, Mail, Hop as Home, Globe, Shield, CircleCheck as CheckCircle, TriangleAlert as AlertTriangle, Camera } from "lucide-react";
import BrandLogo from "../components/shared/BrandLogo";

const SetupPage = () => {
  const { locketId } = useParams();
  const navigate = useNavigate();
  const { login, token, isAuthenticated } = useAuth();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();
  const [error, setError] = React.useState(null);
  const [preFilled, setPreFilled] = React.useState(false);

  // Pre-fill from authenticated user
  React.useEffect(() => {
    if (!isAuthenticated || !token) return;
    const prefill = async () => {
      try {
        const res = await api.get("/qr/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const profile = res.data?.profile;
        if (profile) {
          if (profile.name) setValue("name", profile.name);
          if (profile.phone) setValue("phone", profile.phone);
          if (profile.email) setValue("email", profile.email);
          if (profile.address) setValue("address", profile.address);
          setPreFilled(true);
        }
      } catch (_) {}
    };
    prefill();
  }, [isAuthenticated, token, setValue]);
  const [ownerPhoto, setOwnerPhoto] = React.useState(null);
  const photoInputRef = React.useRef(null);
  const [showCamera, setShowCamera] = React.useState(false);
  const [cameraError, setCameraError] = React.useState(null);
  const streamRef = React.useRef(null);
  const videoRef = React.useRef(null);

  const passwordValue = watch("password", "");
  const confirmPasswordValue = watch("confirmPassword", "");

  // Password strength checks
  const passwordChecks = {
    length: passwordValue.length >= 8,
    uppercase: /[A-Z]/.test(passwordValue),
    lowercase: /[a-z]/.test(passwordValue),
    number: /[0-9]/.test(passwordValue),
  };
  const allPasswordChecksPassed = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = passwordValue === confirmPasswordValue && confirmPasswordValue.length > 0;

  const compressImage = (dataUrl, maxSize = 400, quality = 0.7) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = dataUrl;
    });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const compressed = await compressImage(reader.result, 400, 0.7);
      setOwnerPhoto(compressed);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const startCamera = async () => {
    setCameraError(null);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setCameraError(
        err.name === "NotAllowedError"
          ? "Camera permission denied. Allow camera in browser settings or upload a photo instead."
          : "Camera not available. Use 'Upload photo' below or check that the camera is connected."
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setShowCamera(false);
    setCameraError(null);
  };

  const captureFromCamera = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const raw = canvas.toDataURL("image/jpeg", 0.85);
    const compressed = await compressImage(raw, 400, 0.7);
    setOwnerPhoto(compressed);
    stopCamera();
  };

  React.useEffect(() => {
    if (showCamera && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [showCamera]);

  const onSubmit = async (data) => {
    setError(null);
    try {
      const payload = { ...data };
      if (ownerPhoto) payload.photo = ownerPhoto;
      const res = await api.post(`/qr/${locketId}/setup`, payload);
      if (res.data?.token) {
        login(res.data.token);
      }
      navigate(`/${locketId}/info`);
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.details) {
        setError(errorData.details.map((d) => d.message).join(". "));
      } else {
        setError(errorData?.error || "Setup failed. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200/50 shadow-sm">
        <div className="max-w-lg mx-auto px-3 py-2">
          <BrandLogo logoClassName="h-7" />
        </div>
      </div>
      <div className="max-w-lg mx-auto px-3 pt-3 pb-4">
        {/* Header — compact inline */}
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-red-50 rounded-lg border border-red-200 shrink-0">
            <Shield size={20} className="text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-extrabold text-slate-800 leading-tight">
              Set Up Your QR
            </h1>
            <p className="text-xs text-slate-500">
              {locketId} &middot; Takes 2 minutes
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-slate-200">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {preFilled && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                Your details have been pre-filled from your account. All fields remain editable.
              </div>
            )}
            {/* Password Field */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                <Lock size={14} className="text-slate-500" />
                Password <span className="text-red-600">*</span>
              </label>
              <input
                type="password"
                {...register("password", {
                  required: "Password is required",
                  minLength: { value: 8, message: "Minimum 8 characters" },
                  validate: {
                    uppercase: (v) => /[A-Z]/.test(v) || "Must contain uppercase letter",
                    lowercase: (v) => /[a-z]/.test(v) || "Must contain lowercase letter",
                    number: (v) => /[0-9]/.test(v) || "Must contain a number",
                  },
                })}
                className={`w-full px-3 py-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
                  errors.password
                    ? "border-red-400 focus:ring-red-200"
                    : "border-slate-300 focus:ring-blue-200 focus:border-blue-400"
                }`}
                placeholder="Create a strong password"
              />
              {/* Password strength indicators */}
              {passwordValue.length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {[
                    { check: passwordChecks.length, label: "8+ characters" },
                    { check: passwordChecks.uppercase, label: "Uppercase (A-Z)" },
                    { check: passwordChecks.lowercase, label: "Lowercase (a-z)" },
                    { check: passwordChecks.number, label: "Number (0-9)" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-1">
                      {item.check ? (
                        <CheckCircle size={12} className="text-green-500" />
                      ) : (
                        <AlertTriangle size={12} className="text-amber-500" />
                      )}
                      <span className={`text-xs ${item.check ? "text-green-600" : "text-amber-600"}`}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                <Lock size={14} className="text-slate-500" />
                Confirm Password <span className="text-red-600">*</span>
              </label>
              <input
                type="password"
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (v) => v === passwordValue || "Passwords do not match",
                })}
                className={`w-full px-3 py-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
                  errors.confirmPassword
                    ? "border-red-400 focus:ring-red-200"
                    : confirmPasswordValue && confirmPasswordValue === passwordValue
                    ? "border-green-400 focus:ring-green-200"
                    : "border-slate-300 focus:ring-blue-200 focus:border-blue-400"
                }`}
                placeholder="Re-enter your password"
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
              )}
              {!errors.confirmPassword && confirmPasswordValue && confirmPasswordValue === passwordValue && (
                <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                  <CheckCircle size={12} /> Passwords match
                </p>
              )}
            </div>

            {/* Name Field */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                <User size={14} className="text-slate-500" />
                Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                {...register("name", { required: "Name is required" })}
                className={`w-full px-3 py-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
                  errors.name
                    ? "border-red-400 focus:ring-red-200"
                    : "border-slate-300 focus:ring-blue-200 focus:border-blue-400"
                }`}
                placeholder="e.g., Rahul Sharma"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
            </div>

            {/* Phone Field */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                <Phone size={14} className="text-slate-500" />
                Phone (Indian 10-digit) <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                {...register("phone", {
                  required: "Phone number is required",
                  pattern: { value: /^[6-9]\d{9}$/, message: "Valid Indian phone required (10 digits starting 6-9)" },
                })}
                className={`w-full px-3 py-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
                  errors.phone
                    ? "border-red-400 focus:ring-red-200"
                    : "border-slate-300 focus:ring-blue-200 focus:border-blue-400"
                }`}
                placeholder="e.g., 9876543210"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
            </div>

            {/* Email Field */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                <Mail size={14} className="text-slate-500" />
                Email <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Valid email required" },
                })}
                className={`w-full px-3 py-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
                  errors.email
                    ? "border-red-400 focus:ring-red-200"
                    : "border-slate-300 focus:ring-blue-200 focus:border-blue-400"
                }`}
                placeholder="e.g., rahul@gmail.com"
              />
              <p className="text-slate-400 text-xs mt-1">For password recovery</p>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>

            {/* Address Field */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                <Home size={14} className="text-slate-500" />
                Address (optional)
              </label>
              <textarea
                {...register("address", { maxLength: { value: 500, message: "Max 500 characters" } })}
                rows={2}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors duration-150 resize-vertical min-h-[60px]"
                placeholder="e.g., Flat 302, Green Park, New Delhi"
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address.message}</p>}
            </div>

            {/* Language Field */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                <Globe size={14} className="text-slate-500" />
                Preferred Language
              </label>
              <select
                {...register("preferredLanguage")}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors duration-150 bg-white"
              >
                <option value="both">Both (English & Hindi)</option>
                <option value="english">English</option>
                <option value="hindi">Hindi</option>
              </select>
            </div>

            {/* Owner Photo - for validation on public page */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                <Camera size={14} className="text-slate-500" />
                Your photo (owner of this QR sticker)
              </label>
              <p className="text-xs text-slate-500 mb-2">Shown publicly for identity verification</p>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
              <div className="flex items-center gap-4 flex-wrap">
                {ownerPhoto ? (
                  <>
                    <img
                      src={ownerPhoto}
                      alt="Owner"
                      className="w-20 h-20 rounded-lg object-cover border-2 border-slate-200"
                    />
                    <div className="flex-1 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={startCamera}
                        className="text-sm font-semibold text-red-600 hover:text-red-700"
                      >
                        Take new photo
                      </button>
                      <span className="text-slate-400">|</span>
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        className="text-sm font-semibold text-slate-600 hover:text-slate-800"
                      >
                        Upload different photo
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2 w-full">
                    <button
                      type="button"
                      onClick={startCamera}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-400 transition-colors duration-150 font-semibold"
                    >
                      <Camera size={20} />
                      Open camera
                    </button>
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-50 transition-colors duration-150 font-semibold"
                    >
                      Upload photo
                    </button>
                  </div>
                )}
              </div>
              {cameraError && !showCamera && (
                <p className="text-sm text-amber-700 mt-2 font-medium">{cameraError}</p>
              )}
            </div>

            {/* Camera modal */}
            {showCamera && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
                onClick={stopCamera}
              >
                <div
                  className="bg-white rounded-lg shadow-md max-w-md w-full overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                    <span className="font-bold text-slate-800">Take your photo</span>
                    <button
                      type="button"
                      onClick={stopCamera}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="p-4">
                    <div className="relative rounded-lg overflow-hidden bg-slate-900 aspect-[4/3]">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button
                        type="button"
                        onClick={captureFromCamera}
                        className="flex-1 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold"
                      >
                        Take photo
                      </button>
                      <button
                        type="button"
                        onClick={stopCamera}
                        className="px-4 py-3 rounded-lg border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !allPasswordChecksPassed || !passwordsMatch}
              className={`w-full mt-4 py-3 rounded-lg text-white font-bold text-base shadow-md transition-colors duration-150 ${
                isSubmitting || !allPasswordChecksPassed || !passwordsMatch
                  ? "bg-slate-400 cursor-not-allowed opacity-60"
                  : "bg-red-600 hover:bg-red-700 cursor-pointer"
              }`}
            >
              {isSubmitting ? "Creating your account..." : "Continue — Add Emergency Info →"}
            </button>

            {/* Error Display */}
            {error && (
              <div className="mt-3 p-3 bg-red-50 border-2 border-red-300 rounded-lg text-red-700 text-sm text-center font-medium">
                {error}
              </div>
            )}
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-3">
          Encrypted. You control visibility.
        </p>
      </div>
    </div>
  );
};

export default SetupPage;
